import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import type { GoalReminderFrequency } from '@/types/goals'
import { GOAL_LIMITS, type GoalCycleDraft } from './goalsUtils'

const FieldError = ({ text }: { text?: string }) => (text ? <p role="alert" className="text-xs text-red-600">{text}</p> : null)
const FREQUENCIES: GoalReminderFrequency[] = ['WEEKLY', 'BIWEEKLY', 'OFF']

/**
 * A goals cycle's title and period (criterion 2), and, when `reminder` is
 * given, the check-in reminder (criterion 11: weekly by default, every two
 * weeks or off). Used by Create Space, "Novo ciclo" and the cycle settings.
 */
export const GoalCycleFields = ({ draft, onChange, errors, disabled = false, reminder, onReminderChange, idPrefix = 'goal-cycle' }: {
  draft: GoalCycleDraft
  onChange: (d: GoalCycleDraft) => void
  errors?: Partial<Record<keyof GoalCycleDraft, string>> | null
  disabled?: boolean
  reminder?: GoalReminderFrequency
  onReminderChange?: (f: GoalReminderFrequency) => void
  idPrefix?: string
}) => {
  const { t } = useTranslation('goals')
  const set = (patch: Partial<GoalCycleDraft>) => onChange({ ...draft, ...patch })
  return (
    <div className="min-w-0 space-y-5">
      <div className="space-y-1.5">
        <label htmlFor={`${idPrefix}-title`} className="text-sm font-medium text-gray-700">{t('cycleForm.title')} <span className="text-red-500">*</span></label>
        <Input id={`${idPrefix}-title`} value={draft.title} maxLength={GOAL_LIMITS.cycleTitle} disabled={disabled} placeholder={t('cycleForm.titlePlaceholder')}
          aria-invalid={Boolean(errors?.title)} onChange={(e) => set({ title: e.target.value })} data-testid={`${idPrefix}-title`} />
        <FieldError text={errors?.title} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="min-w-0 space-y-1.5">
          <label htmlFor={`${idPrefix}-start`} className="text-sm font-medium text-gray-700">{t('cycleForm.startsOn')}</label>
          <Input id={`${idPrefix}-start`} type="date" value={draft.startsOn} disabled={disabled} onChange={(e) => set({ startsOn: e.target.value })} />
          <FieldError text={errors?.startsOn} />
        </div>
        <div className="min-w-0 space-y-1.5">
          <label htmlFor={`${idPrefix}-end`} className="text-sm font-medium text-gray-700">{t('cycleForm.endsOn')}</label>
          <Input id={`${idPrefix}-end`} type="date" value={draft.endsOn} min={draft.startsOn || undefined} disabled={disabled} onChange={(e) => set({ endsOn: e.target.value })} />
          <FieldError text={errors?.endsOn} />
        </div>
      </div>
      {reminder && onReminderChange && (
        <div className="space-y-2">
          <span className="text-sm font-medium text-gray-700">{t('cycleForm.reminder')}</span>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('cycleForm.reminder')}>
            {FREQUENCIES.map((f) => (
              <button key={f} type="button" role="radio" aria-checked={reminder === f} disabled={disabled} onClick={() => onReminderChange(f)}
                className={cn('rounded-lg px-4 py-2 text-sm font-medium', reminder === f ? 'bg-primary text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')}>
                {t(`cycleForm.reminders.${f}`)}
              </button>
            ))}
          </div>
          <p className="text-xs text-gray-500">{t(`cycleForm.reminderHint.${reminder}`)}</p>
        </div>
      )}
    </div>
  )
}
